import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getClientsByProperty, associateClientProperty } from '../../Redux/Actions/actions';
import styles from './PropertyClients.module.css';

const PropertyClients = ({ propertyId, onClose }) => {
  const dispatch = useDispatch();
  const { clientsByProperty, clientsLoading, clientsError } = useSelector(state => state);
  
  const [showAssociateForm, setShowAssociateForm] = useState(false);
  const [associationData, setAssociationData] = useState({
    clientId: '',
    notas: '',
    estado: 'interesado'
  });

  useEffect(() => {
    if (propertyId) {
      dispatch(getClientsByProperty(propertyId));
    }
  }, [propertyId, dispatch]);

  const handleAssociation = async (e) => {
    e.preventDefault();
    if (!associationData.clientId) return;

    try {
      await dispatch(associateClientProperty(
        associationData.clientId,
        propertyId,
        {
          notas: associationData.notas,
          estado: associationData.estado
        }
      ));
      
      // Refresh the list
      dispatch(getClientsByProperty(propertyId));
      
      // Reset form
      setAssociationData({
        clientId: '',
        notas: '',
        estado: 'interesado'
      });
      setShowAssociateForm(false);
    } catch (error) {
      console.error('Error al asociar cliente:', error);
    }
  };

  const getStatusColor = (estado) => {
    const colors = {
      'interesado': '#ffc107',
      'contactado': '#17a2b8',
      'visitado': '#6f42c1',
      'negociando': '#fd7e14',
      'cerrado': '#28a745',
      'descartado': '#dc3545'
    };
    return colors[estado] || '#6c757d';
  };

  const getStatusText = (estado) => {
    const texts = {
      'interesado': 'Interesado',
      'contactado': 'Contactado',
      'visitado': 'Visitado',
      'negociando': 'Negociando',
      'cerrado': 'Cerrado',
      'descartado': 'Descartado'
    };
    return texts[estado] || estado;
  };

  if (clientsLoading) {
    return (
      <div className={styles.modal}>
        <div className={styles.modalContent}>
          <div className={styles.loading}>
            <div className={styles.spinner}></div>
            <p>Cargando clientes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.modal}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h2>Clientes Interesados en esta Propiedad</h2>
          <button onClick={onClose} className={styles.closeButton}>
            ✕
          </button>
        </div>

        {clientsError && (
          <div className={styles.errorMessage}>
            {clientsError}
          </div>
        )}

        <div className={styles.actions}>
          <button 
            onClick={() => setShowAssociateForm(true)}
            className={styles.addButton}
          >
            + Asociar Cliente
          </button>
        </div>

        {showAssociateForm && (
          <form onSubmit={handleAssociation} className={styles.associateForm}>
            <h3>Asociar Cliente a la Propiedad</h3>
            
            <div className={styles.formGroup}>
              <label>Cliente:</label>
              <select
                value={associationData.clientId}
                onChange={(e) => setAssociationData(prev => ({
                  ...prev,
                  clientId: e.target.value
                }))}
                required
              >
                <option value="">Seleccionar cliente...</option>
                {/* Note: You might want to load available clients here */}
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Estado:</label>
              <select
                value={associationData.estado}
                onChange={(e) => setAssociationData(prev => ({
                  ...prev,
                  estado: e.target.value
                }))}
              >
                <option value="interesado">Interesado</option>
                <option value="contactado">Contactado</option>
                <option value="visitado">Visitado</option>
                <option value="negociando">Negociando</option>
                <option value="cerrado">Cerrado</option>
                <option value="descartado">Descartado</option>
              </select>
            </div>

            <div className={styles.formGroup}>
              <label>Notas:</label>
              <textarea
                value={associationData.notas}
                onChange={(e) => setAssociationData(prev => ({
                  ...prev,
                  notas: e.target.value
                }))}
                placeholder="Notas sobre el interés del cliente..."
                rows="3"
              />
            </div>

            <div className={styles.formButtons}>
              <button 
                type="button"
                onClick={() => setShowAssociateForm(false)}
                className={styles.cancelButton}
              >
                Cancelar
              </button>
              <button type="submit" className={styles.submitButton}>
                Asociar Cliente
              </button>
            </div>
          </form>
        )}

        <div className={styles.clientsList}>
          {clientsByProperty.length === 0 ? (
            <div className={styles.emptyState}>
              <h3>No hay clientes interesados</h3>
              <p>Aún no hay clientes asociados a esta propiedad.</p>
            </div>
          ) : (
            clientsByProperty.map(client => (
              <div key={client.id} className={styles.clientItem}>
                <div className={styles.clientInfo}>
                  <div className={styles.clientHeader}>
                    <h4>{client.nombre} {client.apellido}</h4>
                    <span 
                      className={styles.status}
                      style={{ backgroundColor: getStatusColor(client.ClientProperty?.estado) }}
                    >
                      {getStatusText(client.ClientProperty?.estado)}
                    </span>
                  </div>
                  
                  <div className={styles.clientDetails}>
                    <div className={styles.detail}>
                      <span className={styles.label}>📧 Email:</span>
                      <span>{client.mail}</span>
                    </div>
                    <div className={styles.detail}>
                      <span className={styles.label}>📞 Teléfono:</span>
                      <span>{client.telefono}</span>
                    </div>
                    {client.categoria && (
                      <div className={styles.detail}>
                        <span className={styles.label}>🏷️ Categoría:</span>
                        <span>{client.categoria}</span>
                      </div>
                    )}
                    {client.ClientProperty?.notas && (
                      <div className={styles.detail}>
                        <span className={styles.label}>📝 Notas:</span>
                        <span>{client.ClientProperty.notas}</span>
                      </div>
                    )}
                    {client.ClientProperty?.fechaInteres && (
                      <div className={styles.detail}>
                        <span className={styles.label}>📅 Fecha de interés:</span>
                        <span>{new Date(client.ClientProperty.fechaInteres).toLocaleDateString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default PropertyClients;